import React from 'react';
import { Clock, Calendar, AlertTriangle, TrendingDown, TrendingUp, User, MapPin, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Player } from '../../types/player';

interface PlayerWithContract extends Player {
  dias_restantes: number;
}

interface ContractCardProps {
  player: PlayerWithContract;
}

const ContractCard: React.FC<ContractCardProps> = ({ player }) => {
  
  // Helper to get player name (new or legacy field)
  // Removes "(id)" format from player names
  const getPlayerName = () => {
    const name = player.player_name || player.nombre_transfermarkt || 'Jugador Desconocido';
    // Remove "(id)" format - extract only the name before the opening parenthesis
    const cleanName = name.split('(')[0].trim();
    return cleanName || name; // fallback to original if empty
  };
  const playerName = getPlayerName();
  
  // Helper to get player position (new or legacy field)
  const playerPosition = player.position || player.posicion_transfermarkt || 'N/A';
  
  // Helper to get player team (new or legacy field)
  const playerTeam = player.current_club_name || player.equipo_transfermarkt || 'N/A';
  
  // Helper to get player age (new or legacy field)
  const playerAge = player.date_of_birth 
    ? new Date().getFullYear() - new Date(player.date_of_birth).getFullYear()
    : player.edad_transfermarkt || 0;
  
  // Helper to get player image (new or legacy field)
  const playerImage = player.player_image_url || player.url_foto_jugador || 'https://images.pexels.com/photos/1618200/pexels-photo-1618200.jpeg';
  
  // Helper to get contract date (new or legacy field)
  const contractDate = player.contract_expires || player.fecha_fin_contrato;
  
  // Helper function to determine contract period
  const getContractPeriod = (dias: number) => {
    const currentDate = new Date();
    const endOfSeason = new Date(currentDate.getFullYear(), 5, 30); // June 30th
    if (currentDate.getMonth() >= 6) { // If we're past June, next season ends next year
      endOfSeason.setFullYear(currentDate.getFullYear() + 1);
    }
    
    const daysToEndOfSeason = Math.ceil((endOfSeason.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (dias < 0) return 'expired';
    if (dias <= daysToEndOfSeason) return 'this_season';
    if (dias <= 365) return 'one_year';
    return 'long_term';
  };

  const getUrgencyColor = (days: number) => {
    const period = getContractPeriod(days);
    switch (period) {
      case 'expired': return {
        bg: 'bg-gradient-to-br from-gray-100 to-gray-200',
        text: 'text-gray-600',
        accent: 'border-gray-300',
        badge: 'bg-gray-100 text-gray-700 border-gray-300'
      };
      case 'this_season': return {
        bg: 'bg-gradient-to-br from-red-50 to-red-100',
        text: 'text-red-700',
        accent: 'border-red-300',
        badge: 'bg-red-100 text-red-700 border-red-300'
      };
      case 'one_year': return {
        bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
        text: 'text-orange-700',
        accent: 'border-orange-300',
        badge: 'bg-orange-100 text-orange-700 border-orange-300'
      };
      case 'long_term': return {
        bg: 'bg-gradient-to-br from-green-50 to-green-100',
        text: 'text-green-700',
        accent: 'border-green-300',
        badge: 'bg-green-100 text-green-700 border-green-300'
      };
      default: return {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
        text: 'text-gray-600',
        accent: 'border-gray-300',
        badge: 'bg-gray-100 text-gray-700 border-gray-300'
      };
    }
  };

  const getUrgencyIcon = (days: number) => {
    const period = getContractPeriod(days);
    switch (period) {
      case 'expired': return <TrendingDown className="h-5 w-5" />;
      case 'this_season': return <AlertTriangle className="h-5 w-5" />;
      case 'one_year': return <Clock className="h-5 w-5" />;
      case 'long_term': return <Calendar className="h-5 w-5" />;
      default: return <Calendar className="h-5 w-5" />;
    }
  };

  const getStatusText = (days: number) => {
    const period = getContractPeriod(days);
    if (days < 0) {
      return `Vencido hace ${Math.abs(days)} días`;
    }
    
    switch (period) {
      case 'this_season': return `Vence en ${days} días`;
      case 'one_year': return `Vence en ${Math.round(days / 30)} meses`;
      case 'long_term': return `Vence en ${Math.round(days / 365)} años`;
      default: return 'Estado desconocido';
    }
  };

  const colors = getUrgencyColor(player.dias_restantes);

  return (
    <div className={`group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-2 border-2 ${colors.accent} h-full flex flex-col`}>
      {/* Background gradient overlay */}
      <div className={`absolute inset-0 ${colors.bg} opacity-30 group-hover:opacity-40 transition-opacity duration-300`}></div>
      
      {/* Status badge floating in top-right */}
      <div className="absolute top-4 right-4 z-10">
        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border ${colors.badge} shadow-sm backdrop-blur-sm`}>
          {getUrgencyIcon(player.dias_restantes)}
          <span className="ml-1.5">
            {getContractPeriod(player.dias_restantes) === 'this_season' && 'URGENTE'}
            {getContractPeriod(player.dias_restantes) === 'one_year' && 'PRÓXIMO'}
            {getContractPeriod(player.dias_restantes) === 'long_term' && 'ESTABLE'}
            {getContractPeriod(player.dias_restantes) === 'expired' && 'LIBRE'}
          </span>
        </div>
      </div>

      <div className="relative z-10 p-6 h-full flex flex-col">
        {/* Player Header */}
        <div className="flex items-start gap-4 mb-4 flex-shrink-0">
          <div className="relative flex-shrink-0">
            <img
              src={playerImage}
              alt={playerName}
              className="w-16 h-16 rounded-2xl object-cover border-3 border-white shadow-lg group-hover:scale-110 transition-transform duration-300"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://images.pexels.com/photos/1618200/pexels-photo-1618200.jpeg';
              }}
            />
            {/* Age badge on image */}
            <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
              {playerAge}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors duration-300 break-words">
              {playerName}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-800">
                <Award className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{playerPosition}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Team and League Info */}
        <div className="space-y-2 mb-4 flex-shrink-0">
          <div className="flex items-center text-sm text-gray-600">
            <User className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="font-medium truncate" title={playerTeam}>
              {playerTeam}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate" title={player.competicion}>
              {player.competicion}
            </span>
          </div>
        </div>

        {/* Contract Status Section */}
        <div className={`p-4 rounded-xl ${colors.bg} border ${colors.accent} mb-4 flex-grow`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {getUrgencyIcon(player.dias_restantes)}
              <span className={`text-sm font-semibold ${colors.text}`}>
                Estado del Contrato
              </span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className={`text-lg font-bold ${colors.text} break-words`}>
              {getStatusText(player.dias_restantes)}
            </div>
            <div className="text-sm text-gray-600 break-words">
              Fin: {contractDate ? new Date(contractDate).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </div>
          </div>
        </div>

        {/* Market Value & Action */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="text-left">
            <div className="text-xs text-gray-500 font-medium">Valor de Mercado</div>
            <div className="text-lg font-bold text-gray-900">
              €{((player.valor_mercado || 0) / 1000000).toFixed(1)}M
            </div>
          </div>
          
          <Link
            to={`/sporting/player/${player.player_id || player.id_fbref}`}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex-shrink-0"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Ver Perfil
          </Link>
        </div>
      </div>

      {/* Hover glow effect */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 blur-xl"></div>
    </div>
  );
};

export default ContractCard;